import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PolicyService, Policy } from '../../shared/services/policy.service';
import { finalize, switchMap } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { OrderService } from 'app/shared/services/order.service';

@Component({
  selector: 'app-policy-list',
  templateUrl: './policy-list.component.html',
  styleUrls: ['./policy-list.component.scss']
})
export class PolicyListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'saleChannel',
    'orderNumber',
    'policyNumber',
    'packageId',
    'orderDate',
    'policyStatus',
    'customerName',
    'customerPhone',
    'customerIdentity',
    'productName',
    'deviceSerial',
    'deviceBrand',
    'company',
    'actions'
  ];

  dataSource: MatTableDataSource<Policy>;
  isLoading = false;
  isValidating = false;
  isDownloading = false;
  errorMessage = '';
  currentUser = '';
  currentToken = '';
  customerPhone = '';
  isTokenValid = false;
  validationMessage = '';
  selectedPolicy: Policy | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private policyService: PolicyService,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.dataSource = new MatTableDataSource<Policy>([]);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      const user = params['user'];

      if (token && user) {
        this.currentToken = token;
        this.currentUser = user;
        this.validateAndLoadPolicies(token, user);
      } else {
        this.errorMessage = 'Token ve kullanıcı parametreleri URL\'de bulunamadı.';
        this.showMessage('Geçersiz URL parametreleri! Token ve user gerekli.', 'error');
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.dataSource.filterPredicate = (data: Policy, filter: string) => {
      const searchStr = filter.toLowerCase();
      return data.orderNumber?.toLowerCase().includes(searchStr) ||
        data.policyNumber?.toLowerCase().includes(searchStr) ||
        data.customerName?.toLowerCase().includes(searchStr) ||
        data.customerPhone?.includes(searchStr) ||
        data.customerIdentity?.includes(searchStr) ||
        data.productName?.toLowerCase().includes(searchStr) ||
        data.deviceSerial?.includes(searchStr);
    };
  }

  formatPhoneNumber(value: string): string {
    if (!value) return '';

    // Sadece rakamları al
    let numbers = value.replace(/\D/g, '');

    // Maksimum 10 haneye sınırla (5551234567)
    if (numbers.length > 10) {
      numbers = numbers.substring(0, 10);
    }

    // Formatla: (XXX) XXX XX XX
    if (numbers.length <= 3) {
      return numbers ? `(${numbers}` : '';
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else if (numbers.length <= 8) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
    }
  }

  onSearch(val: string): void {
    const cleanPhone = val.replace(/\D/g, '');

    if (cleanPhone.length >= 10) {
      this.isLoading = true;

      this.policyService.searchPoliciesByPhone(cleanPhone).subscribe({
        next: (policies) => {
          this.dataSource.data = policies;

          if (policies.length === 0) {
            this.showMessage('Bu telefon numarasına ait poliçe bulunamadı.', 'info');
          } else {
            this.showMessage(`${policies.length} adet poliçe bulundu.`, 'success');
          }
        },
        error: (error) => {
          console.error('Arama hatası:', error);
          this.showMessage('Poliçeler yüklenirken bir hata oluştu!', 'error');
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    } else {
      this.showMessage('Lütfen geçerli bir telefon numarası giriniz.', "error");
    }
  }

  validateAndLoadPolicies(token: string, user: string): void {
    this.isValidating = true;
    this.isLoading = true;
    this.errorMessage = '';
    this.validationMessage = 'Token doğrulanıyor...';

    this.policyService.validateTokenAndUser(token, user)
      .pipe(
        switchMap(validateResponse => {
          if (validateResponse.success && validateResponse.valid) {
            this.isTokenValid = true;
            this.validationMessage = 'Token doğrulandı. Poliçeler yükleniyor...';

            return this.policyService.getCallRecordByToken(token);
          } else {
            this.isTokenValid = false;
            return throwError(() => new Error(validateResponse.message || 'Token doğrulaması başarısız!'));
          }
        }),
        switchMap(recordResponse => {
          if (recordResponse.success && recordResponse.record) {
            this.customerPhone = recordResponse.record.phone;
            return this.policyService.searchPoliciesByPhone(this.customerPhone);
          } else {
            return throwError(() => new Error('Call kaydı bulunamadı!'));
          }
        }),
        finalize(() => {
          this.isValidating = false;
          this.isLoading = false;
          this.validationMessage = '';
        })
      )
      .subscribe({
        next: (policies) => {
          this.dataSource.data = policies;

          if (policies.length === 0) {
            this.showMessage('Bu telefon numarasına ait poliçe bulunamadı.', 'info');
          } else {
            this.showMessage(`${policies.length} adet poliçe bulundu.`, 'success');
          }
        },
        error: (error) => {
          console.error('Error in validation/loading process:', error);
          this.isTokenValid = false;
          this.errorMessage = error.message || 'Token doğrulama veya poliçe yükleme hatası!';
          this.showMessage(this.errorMessage, 'error');
        }
      });
  }

  retryLoading(): void {
    if (this.currentToken && this.currentUser) {
      this.validateAndLoadPolicies(this.currentToken, this.currentUser);
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  selectPolicy(policy: Policy): void {
    this.selectedPolicy = policy;

    this.showMessage(`Poliçe seçildi: ${policy.policyNumber}`, 'success');

    this.sendPolicySelection(policy);

  }

  private sendPolicySelection(policy: Policy): void {
    const selectionData = {
      token: this.currentToken,
      user: this.currentUser,
      policyNumber: policy.policyNumber,
      orderNumber: policy.orderNumber,
      selectedAt: new Date().toISOString()
    };

    console.log('Seçilen poliçe:', selectionData);
  }

  downloadPolicy(policy: Policy): void {
    if (!policy.company || !policy.policyNumber) {
      this.showMessage('Sigorta şirketi veya poliçe numarası eksik!', 'error');
      return;
    }

    this.isDownloading = true;

    this.orderService.getPolicyDocument(policy.company, policy.policyNumber)
      .pipe(
        finalize(() => {
          this.isDownloading = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response && response.filename && response.fileBase64Data) {
            try {
              const byteCharacters = atob(response.fileBase64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'application/pdf' });

              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `police_${policy.policyNumber}.pdf`;
              document.body.appendChild(link);
              link.click();

              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              this.showMessage('Poliçe başarıyla indirildi!', 'success');
            } catch (e) {
              console.error('PDF dönüştürme hatası:', e);
              this.showMessage('Dosya formatı dönüştürülürken hata oluştu.', 'error');
            }
          } else {
            this.showMessage(response.error || 'Poliçe dökümanı bulunamadı.', 'error');
          }
        },
        error: (error) => {
          console.error('Error downloading policy:', error);
          this.showMessage('Poliçe indirilirken bir hata oluştu!', 'error');
        }
      });
  }

  showPolicyDetails(policy: Policy): void {
    console.log('Poliçe detayları:', policy);

  }

  clearSelection(): void {
    this.selectedPolicy = null;
    this.showMessage('Seçim iptal edildi', 'info');
  }

  isPolicySelected(policy: Policy): boolean {
    return this.selectedPolicy?.policyNumber === policy.policyNumber;
  }

  private showMessage(message: string, type: 'success' | 'error' | 'info'): void {
    const panelClass = type === 'error' ? 'error-snackbar' :
      type === 'success' ? 'success-snackbar' :
        'info-snackbar';

    this.snackBar.open(message, 'Kapat', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  formatPhone(phone: string): string {
    if (!phone) return '-';
    if (phone.length === 10) {
      return phone.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '($1) $2 $3 $4');
    }
    return phone;
  }

  formatDate(date: Date | string): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('tr-TR');
  }

  maskIdentity(identity: string): string {
    if (!identity || identity.length !== 11) return identity;
    return identity.substring(0, 3) + '******' + identity.substring(9);
  }

}